import styled, { css } from 'styled-components';
import { overlayShadow } from '../../../../themes/shadows';

// Fundo dos paineis flutuantes do TELAO (docks + cards de totais). Voltou
// a ser SOLIDO: o liquid glass (backdrop-filter) nao renderiza bem no
// navegador da TV — os paineis ficavam transparentes e ilegiveis sobre o
// mapa. Solido garante leitura em qualquer navegador.
const liquidGlass = css`
  background: var(--cco-bg);
  border: 1px solid var(--cco-line);
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.45);
`;

export const Container = styled.div`
  width: 100%;
  min-height: 322px;
  height: 100%;
  display: flex;
  justify-content: space-between;
  background: ${(props) => (props.$cco ? 'var(--cco-bg)' : 'transparent')};

  @media (max-width: 1300px) {
    flex-direction: column;
    justify-content: space-between !important;
  }

  /* Em embedded mantemos display:flex (necessario pro Google Maps
     medir corretamente o container na inicializacao — sem flex, o
     mapa nao baixa as tiles). Soh trocamos justify-content pra
     flex-start pra garantir que o unico filho (ErrorMapContainer)
     cole na esquerda em vez de competir com space-between. */
  ${(props) =>
    props.$embedded &&
    `
      justify-content: flex-start;
    `}

  /* CCO layout (Dashboard.html X/Twitter): grid 3-col com hairlines
     entre colunas. Larguras fluidas com clamp pra adaptar a viewport:
     filtros 280-380, alertas 280-360, mapa preenche o resto (1fr).
     Em viewport estreita colapsa pra 1-col. */
  ${(props) =>
    props.$cco &&
    `
      display: grid;
      /* Coluna direita (AlertOverlay) ampliada de 280-360px pra 320-440px
         pra acomodar causas longas tipo 'TEMPERATURA ESTATOR FASE S -
         ERRADA · AGORA' numa linha so. Mapa continua flex 1fr e absorve
         a diferenca em viewports largos. */
      grid-template-columns: clamp(280px, 24vw, 380px) 1fr clamp(320px, 26vw, 440px);
      justify-content: unset;
      color: var(--cco-ink);
      min-width: 0;

      > * + * {
        border-left: 1px solid var(--cco-line);
      }

      @media (max-width: 1100px) {
        grid-template-columns: 1fr;
        grid-template-rows: auto 60vh auto;
        > * + * {
          border-left: none;
          border-top: 1px solid var(--cco-line);
        }
      }

      /* Mobile (<= 720px): no /dashboard ListsWrapper e AlertOverlay
         estao display:none (a tela tem tabs proprias). Resta so o
         mapa — colapsa o grid pra uma unica linha 1fr que enche o
         wrapper (MobileMapWrapper define a altura). Sem isso, as
         rows minimas das filhas escondidas reservavam espaco inutil. */
      @media (max-width: 720px) {
        grid-template-rows: 1fr;
        height: 100%;
      }
    `}
`;

export const Icon = styled.img`
  width: ${(props) => props.size};
  height: ${(props) => props.size};
  cursor: pointer;
`;

// Botao "Voltar" do drill-down: aparece no topo da lista de clientes
// (cco mode), permite voltar pra lista de estados. Estilo discreto
// alinhado com o resto do painel.
export const CcoDrillBack = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 14px var(--cco-pad);
  border: none;
  border-bottom: 1px solid var(--cco-line);
  background: transparent;
  color: var(--cco-ink-mute);
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  cursor: pointer;
  font-family: inherit;
  transition: color 150ms;

  > svg {
    width: 14px;
    height: 14px;
  }

  &:hover {
    color: var(--cco-ink);
  }
`;

// Search bar custom pro modo CCO — substitui o <Input> generico
// (que tem margins/padding legados). Estrutura simples: ícone SVG +
// input transparent, hairline border-bottom.
export const CcoSearchBar = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 18px var(--cco-pad);
  border-bottom: 1px solid var(--cco-line);

  > svg {
    color: var(--cco-ink-mute);
    flex: none;
  }
  > input {
    flex: 1;
    min-width: 0;
    background: transparent;
    border: 0;
    outline: 0;
    color: var(--cco-ink);
    font-size: 14.5px;
    font-family: inherit;

    &::placeholder {
      color: var(--cco-ink-mute);
    }
  }
`;

// ListsWrapper — em $cco vira coluna 1 do grid (flush, sem padding
// horizontal, full-height). Em modo legado mantem padding antigo.
export const ListsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 250px;
  gap: 10px;
  position: relative;

  ${(props) =>
    props.$cco
      ? `
        gap: 0;
        padding: 0;
        height: 100%;
        background: var(--cco-bg);
        overflow-y: auto;
      `
      : `
        padding: 15px 10px 15px 0;
      `}

  /* Mobile: o dashboard ja tem tabs (Alertas/Mapa/Visao Geral) — a
     sidebar de zonas/clientes some pra nao competir e nem sobrepor
     o mapa. O Dashboard renderiza o painel de busca/filtros num bottom
     sheet acionado por botao quando precisar. */
  @media (max-width: 720px) {
    display: none;
  }
`;

// Dropdown flutuante embaixo do input de search, mostrando top
// matches (zonas + clientes) clicaveis. Aparece quando search tem
// resultados e some no blur/Esc/click externo.
export const SearchDropdown = styled.div`
  position: absolute;
  top: 50px;
  left: 0;
  width: 375px;
  max-height: 280px;
  overflow-y: auto;
  z-index: 10;
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 6px;
  background: ${(props) => props.theme.colors.card};
  border: 1px solid ${(props) => props.theme.colors.disabled};
  border-radius: 6px;
  box-shadow: none;
  animation: search-dropdown-fade 120ms ease-out;

  @keyframes search-dropdown-fade {
    from {
      opacity: 0;
      transform: translateY(-4px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  ::-webkit-scrollbar {
    width: 4px;
  }
  ::-webkit-scrollbar-thumb {
    background: ${(props) => props.theme.colors.disabled};
    border-radius: 4px;
  }
`;

// Cabecalho de grupo dentro do dropdown ('Zonas', 'Clientes'). So
// renderiza quando ha resultados naquele grupo.
export const SearchDropdownGroupLabel = styled.div`
  font-size: 9px;
  font-weight: 800;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  color: ${(props) => props.theme.colors.label};
  padding: 6px 8px 2px;
`;

// Item clicavel — visual de pill com nome do resultado + tipo (zona
// ou cliente). Hover destaca pra ficar claro que e interativo.
export const SearchDropdownItem = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 7px 9px;
  background: transparent;
  border: 1px solid transparent;
  border-radius: 6px;
  cursor: pointer;
  text-align: left;
  width: 100%;
  color: ${(props) => props.theme.colors.darkerblue};
  font-size: 12px;
  font-weight: 600;
  transition: background 120ms, border-color 120ms;

  > .item-icon {
    flex-shrink: 0;
    display: inline-flex;
    color: ${(props) => props.theme.colors.lightblue};
  }
  svg {
    width: 14px;
    height: 14px;
    stroke: currentColor;
    stroke-width: 2;
    fill: none;
    stroke-linecap: round;
    stroke-linejoin: round;
  }

  > .item-name {
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &:hover {
    background: ${(props) =>
      props.theme.title === 'light'
        ? props.theme.colors.disabled
        : 'rgba(255, 255, 255, 0.05)'};
    border-color: ${(props) => props.theme.colors.lightblue};
  }
`;

// Estado vazio (busca digitada mas sem matches) — mostrado dentro
// do dropdown pra dar feedback que algo aconteceu.
export const SearchDropdownEmpty = styled.div`
  padding: 12px;
  font-size: 11px;
  color: ${(props) => props.theme.colors.label};
  text-align: center;
`;

// Overlay flutuante no canto direito do mapa (modo legado), com altura
// total. Em $cco vira coluna 3 do grid (sibling, nao absolute).
export const AlertOverlay = styled.div`
  ${(props) =>
    props.$cco
      ? `
        display: flex;
        flex-direction: column;
        gap: 0;
        padding: 0;
        width: 100%;
        height: 100%;
        background: var(--cco-bg);
        border: none;
        border-radius: 0;
        box-shadow: none;
        /* overflow hidden no container: o HEADER fica fixo (flex-shrink:0)
           e SO o AlertList rola (altura do card preservada). Antes o
           overflow-y:auto aqui fazia o header rolar junto + scroll duplo
           com o AlertList. */
        overflow: hidden;
        min-height: 0;

        /* Mobile: o /dashboard tem aba "Alertas" dedicada (standalone
           AlertOverlay renderizado no nivel do dashboard). Esconde a
           coluna 3 do grid $cco pra aba "Mapa" ficar so com o mapa. */
        @media (max-width: 720px) {
          display: none;
        }
      `
      : `
        position: absolute;
        top: 12px;
        right: 12px;
        bottom: 12px;
        z-index: 4;
        display: flex;
        flex-direction: column;
        gap: 8px;
        padding: 12px;
        width: 240px;
        background: ${props.theme.colors.card};
        border: 1px solid ${props.theme.colors.red};
        border-radius: 6px;
        box-shadow: none;

        /* Mobile: tambem esconde o overlay legado (em /dashboard/customer
           etc) — alertas tem espacos dedicados nas telas mobile. */
        @media (max-width: 720px) {
          display: none;
        }
      `}
`;

// Dock do painel de alertas/atividade quando o mapa entra em TELA CHEIA
// (modo TV/CCO). No fullscreen so o #dashboard-map-container fica visivel,
// entao a coluna 3 do grid (overlay normal) some — este dock re-monta o
// painel ABSOLUTO dentro do MapContainer, na lateral direita, como card
// flutuante sobre o mapa. Renderizado so quando isFullscreen && ccoLayout.
export const FullscreenAlertDock = styled.div`
  position: absolute;
  top: 16px;
  right: 16px;
  bottom: 16px;
  width: 360px;
  max-width: 32vw;
  z-index: 6;
  display: flex;
  flex-direction: column;
  border-radius: 14px;
  overflow: hidden;
  ${liquidGlass}

  @media (max-width: 720px) {
    display: none;
  }
`;

// === Dock "TOP GERACAO" no telao (lateral ESQUERDA) — espelha o dock de
// alertas da direita. Cards das maquinas que mais geram + legenda compacta
// no rodape (substitui a MapLegend solta). ===
export const FullscreenTopGenDock = styled.div`
  position: absolute;
  top: 16px;
  left: 16px;
  bottom: 16px;
  width: 320px;
  max-width: 28vw;
  z-index: 6;
  display: flex;
  flex-direction: column;
  border-radius: 14px;
  overflow: hidden;
  ${liquidGlass}

  @media (max-width: 720px) {
    display: none;
  }
`;

// === Pad de navegacao manual (pan + zoom) no telao (tela cheia) ===
// Fica na lateral direita, a ESQUERDA do dock de alertas (posicao classica
// de zoom). Util quando o operador nao tem mouse/touch facil (TV/CCO).
export const MapNavPad = styled.div`
  position: absolute;
  top: 50%;
  right: calc(min(360px, 32vw) + 28px);
  transform: translateY(-50%);
  z-index: 6;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;

  @media (max-width: 720px) {
    display: none;
  }
`;

// Cruz direcional 3x3 (so as 4 setas; centro vazio).
export const NavCross = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 40px);
  grid-template-rows: repeat(3, 40px);
  gap: 4px;
`;

export const NavZoom = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

export const NavBtn = styled.button`
  width: 40px;
  height: 40px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  background: var(--cco-surface);
  border: 1px solid var(--cco-line);
  border-radius: 8px;
  color: var(--cco-ink);
  cursor: pointer;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.4);
  transition: background 120ms, border-color 120ms, transform 120ms;

  /* Posicoes na cruz: up(1,2) left(2,1) right(2,3) down(3,2). */
  &[data-dir='up'] {
    grid-column: 2;
    grid-row: 1;
  }
  &[data-dir='left'] {
    grid-column: 1;
    grid-row: 2;
  }
  &[data-dir='right'] {
    grid-column: 3;
    grid-row: 2;
  }
  &[data-dir='down'] {
    grid-column: 2;
    grid-row: 3;
  }

  svg {
    width: 18px;
    height: 18px;
    stroke: currentColor;
    stroke-width: 2.4;
    fill: none;
    stroke-linecap: round;
    stroke-linejoin: round;
  }

  &:hover {
    background: var(--cco-soft-surface);
    border-color: var(--cco-line-strong);
    transform: translateY(-1px);
  }
  &:active {
    transform: translateY(0);
  }
`;

export const TopGenHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 14px var(--cco-pad);
  border-bottom: 1px solid var(--cco-line);
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0.1em;
  color: var(--cco-ink);
  flex-shrink: 0;

  > .dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: var(--cco-green);
    box-shadow: 0 0 0 3px color-mix(in srgb, var(--cco-green) 22%, transparent);
  }
  > .meta {
    margin-left: auto;
    font-size: 10px;
    font-weight: 600;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: var(--cco-ink-faint);
  }
`;

export const TopGenList = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 8px;
  display: flex;
  flex-direction: column;
  gap: 6px;

  scrollbar-width: thin;
  scrollbar-color: var(--cco-line-strong) transparent;
  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-thumb {
    background: var(--cco-line-strong);
    border-radius: 3px;
  }
`;

export const TopGenEmpty = styled.div`
  padding: 24px 12px;
  text-align: center;
  color: var(--cco-ink-faint);
  font-size: 12px;
`;

export const TopGenCard = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 9px 10px;
  border-radius: 9px;
  background: var(--cco-soft-surface);
  border: 1px solid var(--cco-line);
  min-width: 0;
`;

export const TopGenRank = styled.span`
  flex-shrink: 0;
  width: 22px;
  height: 22px;
  border-radius: 6px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 800;
  font-variant-numeric: tabular-nums;
  background: ${(props) =>
    props.$top ? 'var(--cco-orange)' : 'var(--cco-line)'};
  color: ${(props) => (props.$top ? '#1a1208' : 'var(--cco-ink-mute)')};
`;

export const TopGenBody = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 3px;
`;

export const TopGenName = styled.span`
  font-size: 13px;
  font-weight: 700;
  color: var(--cco-ink);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

// Linha de contexto: chip de modelo + (parque · cliente). O sub trunca; o
// chip do modelo fica fixo (nao encolhe) pra ser sempre legivel.
export const TopGenMetaRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
`;

export const TopGenModel = styled.span`
  flex-shrink: 0;
  max-width: 130px;
  padding: 1px 7px;
  border-radius: 5px;
  background: var(--cco-soft-surface);
  border: 1px solid var(--cco-line);
  color: var(--cco-ink-mute);
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.02em;
  font-variant-numeric: tabular-nums;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const TopGenSub = styled.span`
  font-size: 10.5px;
  font-weight: 500;
  color: var(--cco-ink-faint);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
`;

export const TopGenBarTrack = styled.div`
  width: 100%;
  height: 5px;
  border-radius: 999px;
  background: var(--cco-bg);
  border: 1px solid var(--cco-line);
  overflow: hidden;
  margin-top: 2px;
`;

export const TopGenBarFill = styled.div`
  height: 100%;
  border-radius: 999px;
  /* Cor por modo: geracao (UCHA) laranja, consumo (EBA) azul — mesma
     convencao do resto do CCO. */
  background: ${(props) =>
    props.$mode === 'consumption' ? 'var(--cco-blue)' : 'var(--cco-orange)'};
  min-width: 2px;
  transition: width 400ms ease;
`;

// Total de energia acumulado da maquina (kWh->MWh->GWh auto), linha
// discreta abaixo da barra. Complementa a potencia atual (kW) com o
// quanto a maquina ja gerou/consumiu no total.
export const TopGenTotal = styled.span`
  font-size: 10px;
  font-weight: 600;
  color: var(--cco-ink-faint);
  font-variant-numeric: tabular-nums;
  margin-top: 3px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;

  b {
    color: var(--cco-ink-mute);
    font-weight: 800;
  }
`;

// Toggle Geracao/Consumo no header do dock.
export const TopGenModeToggle = styled.div`
  display: inline-flex;
  padding: 2px;
  gap: 2px;
  border-radius: 7px;
  background: var(--cco-soft-surface);
  border: 1px solid var(--cco-line);
`;

export const TopGenModeBtn = styled.button`
  padding: 3px 10px;
  border: none;
  border-radius: 5px;
  background: ${(props) =>
    props.$active
      ? props.$mode === 'consumption'
        ? 'var(--cco-blue)'
        : 'var(--cco-orange)'
      : 'transparent'};
  color: ${(props) =>
    props.$active
      ? props.$mode === 'consumption'
        ? '#fff'
        : '#1a1208'
      : 'var(--cco-ink-mute)'};
  font-size: 10.5px;
  font-weight: 800;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  cursor: pointer;
  font-family: inherit;
  transition: background 150ms, color 150ms;
`;

export const TopGenValue = styled.span`
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  font-size: 14px;
  font-weight: 800;
  color: var(--cco-green);
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
  line-height: 1.15;

  /* Potencia atual abaixo da vazao (modo Bombas) — secundaria. */
  > small {
    font-size: 11px;
    font-weight: 700;
    color: var(--cco-ink-faint);
  }
`;

export const TopGenLegend = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px 12px;
  padding: 10px var(--cco-pad);
  border-top: 1px solid var(--cco-line);
  flex-shrink: 0;
`;

export const TopGenLegendItem = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: transparent;
  border: none;
  padding: 2px 0;
  cursor: pointer;
  font-family: inherit;
  font-size: 11px;
  font-weight: 600;
  color: var(--cco-ink-mute);
  opacity: ${(props) => (props.$active ? 1 : 0.45)};
  transition: opacity 150ms;

  &:hover {
    opacity: ${(props) => (props.$active ? 1 : 0.75)};
  }
`;

export const TopGenLegendDot = styled.span`
  width: 9px;
  height: 9px;
  border-radius: 50%;
  flex-shrink: 0;
  background: ${(props) => (props.$hollow ? 'transparent' : props.color)};
  border: 2px solid ${(props) => props.color};
  box-sizing: border-box;
`;

// === Totais "AGORA" sobrepostos no rodape-central do mapa em TELA CHEIA ===
// Modo TV no CCO: geracao/consumo + vazao agregados (UCHAs e EBAs) flutuando
// no centro-baixo, fora do caminho do FullscreenAlertDock (lateral direita).
const liveAccent = (props) =>
  props.color === 'blue' ? 'var(--cco-blue)' : 'var(--cco-orange)';
const liveAccentSoft = (props) =>
  props.color === 'blue' ? 'var(--cco-blue-soft)' : 'var(--cco-orange-soft)';
const liveAccentBorder = (props) =>
  props.color === 'blue'
    ? 'rgba(78, 161, 243, 0.25)'
    : 'rgba(241, 162, 91, 0.25)';

export const FullscreenLiveTotals = styled.div`
  position: absolute;
  bottom: 24px;
  /* SEMPRE dentro da faixa entre os docks — usa as MESMAS larguras deles
     (esq: Top Geracao 320/28vw; dir: Alertas 360/32vw) + margem. Assim os
     cards nunca sobrepoem os sidebars, em qualquer largura. */
  left: calc(min(320px, 28vw) + 32px);
  right: calc(min(360px, 32vw) + 32px);
  z-index: 6;
  display: flex;
  gap: 14px;
  pointer-events: none;
  justify-content: center;
  align-items: flex-end;
  flex-wrap: nowrap;

  /* Quando a faixa fica estreita pra dois lado-a-lado, EMPILHA (cresce pra
     cima a partir do bottom) e cada card ocupa a faixa toda — em vez de
     espremer/transbordar nos docks. */
  @media (max-width: 1300px) {
    flex-direction: column;
    align-items: stretch;
  }
  /* Sem docks (mobile): esconde os totais, como antes. */
  @media (max-width: 720px) {
    display: none;
  }
`;

export const LiveTotalCard = styled.div`
  pointer-events: auto;
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 14px 26px 16px;
  /* Encolhe pra caber na faixa (min-width:0) em vez de transbordar; no modo
     empilhado o align-items:stretch do pai deixa cada card ocupar a faixa. */
  flex: 0 1 auto;
  min-width: 0;
  border-radius: 14px;
  /* Liquid Glass (mesma receita dos docks/menu) — so aparece no telao,
     pois o card so e renderizado em tela cheia. */
  ${liquidGlass}
`;

// Cabecalho do card: chip "TOTAL DE N UCHAS/EBAS" + meta (Geracao /
// Bombeamento · atualizacao continua) — mesmo padrao do TagTitle do
// card VisaoGeralUchasEbas, separado por uma hairline.
export const LiveTotalTitle = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding-bottom: 10px;
  border-bottom: 1px solid var(--cco-line);
  flex-wrap: wrap;
`;

export const LiveTotalChip = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 6px 12px;
  border-radius: 999px;
  background: ${liveAccentSoft};
  color: ${liveAccent};
  font-size: 11.5px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  box-shadow: inset 0 0 0 1px ${liveAccentBorder};
  white-space: nowrap;
`;

export const LiveTotalMeta = styled.span`
  color: var(--cco-ink-mute);
  font-size: 12px;

  b {
    color: var(--cco-ink);
    font-weight: 600;
  }
`;

export const LiveTotalLabel = styled.div`
  font-size: 11px;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--cco-ink-mute);
  font-weight: 600;
  text-align: center;
`;

export const LiveTotalPair = styled.div`
  display: grid;
  grid-template-columns: 1fr 1px 1fr;
  align-items: stretch;
`;

export const LiveTotalSep = styled.span`
  background: var(--cco-line);
  width: 1px;
  height: 100%;
`;

export const LiveTotalMetric = styled.div`
  padding: 2px 16px;
  text-align: center;
  min-width: 0;
`;

export const LiveTotalHeader = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  font-weight: 600;
  color: ${liveAccent};

  > svg {
    width: 14px;
    height: 14px;
    fill: currentColor;
    flex-shrink: 0;
  }
`;

export const LiveTotalValue = styled.div`
  margin-top: 6px;
  font-size: 24px;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  line-height: 1.1;
  white-space: nowrap;
  /* $positive: verde (gerando/consumindo agora). Senao usa o accent
     do tipo (orange UCHA / blue EBA) — usado na Vazao. */
  color: ${(props) =>
    props.$positive ? 'var(--cco-green)' : liveAccent(props)};

  span,
  small {
    font-size: 14px;
    color: var(--cco-ink-mute);
    font-weight: 500;
    margin-left: 4px;
  }
`;

// Painel lateral de detalhe do parque selecionado. Em $cco vira
// coluna 3 do grid (sibling, full-height); senao mantem o overlay
// absolute top-right legado.
export const MarkerDetailPanel = styled.div`
  /* box-sizing garante que padding seja descontado da height: 100%; sem
     isso a panel ultrapassava o grid em ~24px (12 top + 12 bottom de
     padding), empurrando o SeeMore pra fora do viewport. */
  box-sizing: border-box;
  /* overflow hidden + flex column + DetailContent (overflow-y auto) +
     SeeMore (flex-shrink:0) garante que o conteudo do meio role e o
     botao do rodape fique sempre visivel ancorado ao bottom. */
  overflow: hidden;

  ${(props) =>
    props.$cco
      ? `
        display: flex;
        flex-direction: column;
        gap: 10px;
        padding: 12px 12px 16px;
        width: 100%;
        height: 100%;
        background: var(--cco-bg);
        border: none;
        border-radius: 0;
        box-shadow: none;
      `
      : `
        position: absolute;
        top: 12px;
        right: 12px;
        bottom: 12px;
        z-index: 4;
        display: flex;
        flex-direction: column;
        gap: 10px;
        padding: 12px;
        width: 240px;
        background: ${props.theme.colors.card};
        border: 1px solid ${props.theme.colors.lightblue};
        border-radius: 6px;
        box-shadow: none;
      `}

  /* Anima entrada do painel pra reforcar mudanca de contexto */
  animation: detail-fade-in 180ms ease-out;
  @keyframes detail-fade-in {
    from {
      opacity: 0;
      transform: translateX(8px);
    }
    to {
      opacity: 1;
      transform: translateX(0);
    }
  }

  /* Em modo legado (overlay absolute right/240px) em mobile vira
     fullscreen sobre o mapa pra dar espaco real pra leitura. No
     modo $cco ja vira coluna full-width via fallback do Container. */
  ${(props) =>
    !props.$cco &&
    `
      @media (max-width: 720px) {
        top: 0;
        right: 0;
        bottom: 0;
        left: 0;
        width: 100%;
        z-index: 5;
        border-radius: 0;
      }
    `}
`;

// Botao back/fechar no topo do painel de detalhe. Mostra texto contextual:
// "Em alerta agora (8)" se houver alertas ativos (volta pro overlay de
// alertas), ou "Fechar" se nao houver. Visual de chip clicavel com
// chevron invertido.
export const DetailBackButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  width: 100%;
  padding: 6px 10px;
  background: transparent;
  border: 1px solid ${(props) => props.theme.colors.disabled};
  border-radius: 6px;
  color: ${(props) => props.theme.colors.darkerblue};
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  cursor: pointer;
  transition: all 150ms;

  &::before {
    content: '‹';
    font-size: 18px;
    line-height: 1;
    color: ${(props) => props.theme.colors.label};
    transition: color 150ms, transform 150ms;
  }

  &:hover {
    background: ${(props) =>
      props.theme.title === 'light'
        ? props.theme.colors.disabled
        : 'rgba(255, 255, 255, 0.04)'};
    border-color: ${(props) => props.theme.colors.lightblue};
    &::before {
      color: ${(props) => props.theme.colors.lightblue};
      transform: translateX(-2px);
    }
  }
`;

// Wrapper interno do painel pra dar respiro ao redor do conteudo do
// marker (StatusBadge, ParkTitle, Chips, sections, SeeMore). Padding
// lateral menor — o MarkerDetailPanel ja tem padding 12px externo.
// flex: 1 + min-height: 0 permite que o conteudo intermediario flua
// e o SeeMore (com margin-top: auto) seja empurrado pro fundo.
export const DetailContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  flex: 1;
  min-height: 0;
  gap: 8px;
  padding: 4px 2px 0;
  overflow-y: auto;

  /* Scrollbar discreta caso o conteudo passe da altura do painel
     (parques com muitos equipamentos podem encher). */
  ::-webkit-scrollbar {
    width: 4px;
  }
  ::-webkit-scrollbar-thumb {
    background: ${(props) => props.theme.colors.disabled};
    border-radius: 4px;
  }
`;

// Separador visual entre o bloco de identidade do parque (status + titulo
// + chips) e as sections de informacao adicional. Linha sutil 1px.
export const DetailSeparator = styled.div`
  height: 1px;
  background: ${(props) => props.theme.colors.disabled};
  margin: 6px 0 4px;
  opacity: 0.5;
`;

// Header pequeno de cada section (TEMPO REAL, EQUIPAMENTOS, etc). Mesma
// linguagem visual do SectionTag das outras telas, em escala compacta
// pra caber no painel lateral de 240px.
export const DetailSectionLabel = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 2px 8px;
  border-radius: 6px;
  background: ${(props) =>
    props.theme.title === 'light'
      ? props.theme.colors.darkblue
      : props.theme.colors.lightblue};
  color: ${(props) => props.theme.colors.white};
  font-size: 9px;
  font-weight: 800;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  align-self: flex-start;
  margin-top: 4px;
`;

// Cabecalho de section: label a esquerda + auxiliar (selo de frescor) a
// direita, mesma linha. Resolve o selo espremido dentro do pill.
export const DetailSectionHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-top: 4px;

  /* O label (div) herda margin-top/align-self proprios — zera dentro do
     head pra alinhar com o selo na mesma linha. */
  > div {
    margin-top: 0;
    align-self: center;
  }
`;

// Selo de frescor do dado realtime — pill STANDALONE ao lado do label
// "TEMPO REAL". Verde pulsante = ao vivo; vermelho solido = sem
// atualizacao recente (comunicacao pode ter caido).
export const RealtimeFreshness = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 2px 9px;
  border-radius: 999px;
  background: ${(props) =>
    props.$stale ? 'var(--cco-red)' : 'var(--cco-soft-surface)'};
  border: 1px solid
    ${(props) => (props.$stale ? 'transparent' : 'var(--cco-line)')};
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.02em;
  color: ${(props) => (props.$stale ? '#fff' : 'var(--cco-ink-mute)')};
  font-variant-numeric: tabular-nums;
  white-space: nowrap;

  .dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: ${(props) => (props.$stale ? '#fff' : 'var(--cco-green)')};
    ${(props) =>
      !props.$stale &&
      `
        animation: rt-fresh-pulse 1.8s ease-in-out infinite;
        @keyframes rt-fresh-pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.4; }
        }
      `}
  }
`;

// Cards de metrica tempo real (Vazao/Consumo etc) — grid responsivo, valor
// em destaque. Substitui a linha label-esquerda/valor-direita que deixava
// muito espaco vazio no meio.
export const MetricCards = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(130px, 1fr));
  gap: 8px;
  padding: 6px 0 2px;
`;

export const MetricCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 10px 12px;
  border-radius: 10px;
  background: var(--cco-soft-surface);
  border: 1px solid var(--cco-line);
  min-width: 0;

  > .top {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 11px;
    font-weight: 600;
    color: var(--cco-ink-mute);

    > .ic {
      display: inline-flex;
      align-items: center;
      color: var(--cco-blue);
    }
  }

  > .val {
    font-size: 19px;
    font-weight: 800;
    color: var(--cco-ink);
    font-variant-numeric: tabular-nums;
    line-height: 1.1;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

export const MetricCardSpark = styled.div`
  margin-top: 2px;
  opacity: 0.85;
`;

// Lista de metricas (label + valor). Cada linha justifica label a
// esquerda e valor a direita pra leitura rapida.
export const MetricsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 4px 6px 0;
`;

export const MetricRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 6px;
  font-size: 12px;
`;

export const MetricLabel = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: ${(props) => props.theme.colors.label};
  font-weight: 600;

  /* SVG icon Lucide-style ao lado do label. currentColor herda da cor
     do label, line-cap round consistente com o resto. */
  > .label-icon {
    display: inline-flex;
    align-items: center;
    color: ${(props) => props.theme.colors.lightblue};
  }
`;

export const MetricValue = styled.strong`
  color: ${(props) =>
    props.color || props.theme.colors.darkerblue};
  font-weight: 800;
  font-variant-numeric: tabular-nums;
`;

// Wrapper do Sparkline inline entre o label e o value. flex: 0 cresce
// so o suficiente pra acomodar o grafico (largura fixa no Sparkline).
// margin-left auto empurra o value pra direita.
export const MetricSparkline = styled.div`
  display: inline-flex;
  align-items: center;
  margin-left: auto;
  margin-right: 8px;
  opacity: 0.85;
`;

// Linha de equipamento individual: dot do status + nome + label do status.
export const ApplianceRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  min-width: 0;

  > span.dot {
    width: 9px;
    height: 9px;
    border-radius: 50%;
    background: ${(props) => props.color};
    flex-shrink: 0;
  }
  > .name {
    color: ${(props) => props.theme.colors.darkerblue};
    flex: 1;
    min-width: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    font-weight: 600;
  }
  > .status {
    color: ${(props) => props.color};
    font-weight: 700;
    text-transform: capitalize;
    flex-shrink: 1;
    min-width: 0;
    text-align: right;
    font-size: 11px;

    /* "ultima leitura ha X" — anexo discreto no sem-dados: sem capitalize,
       menor e em tom mute, pra nao competir com o rotulo do status. */
    .ago {
      text-transform: none;
      font-weight: 500;
      font-size: 10px;
      color: ${(props) => props.theme.colors.label};
    }
  }
`;

// Linha do breakdown de equipamentos: dot colorido + label + count.
export const StatusBreakdownRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;

  /* dot status */
  > span.dot {
    width: 9px;
    height: 9px;
    border-radius: 50%;
    background: ${(props) => props.color};
    flex-shrink: 0;
  }
  > .label {
    color: ${(props) => props.theme.colors.darkerblue};
    flex: 1;
    text-transform: capitalize;
  }
  > .count {
    color: ${(props) => props.theme.colors.darkerblue};
    font-weight: 800;
    font-variant-numeric: tabular-nums;
  }
`;

// Lista interna ocupa todo o resto da altura, scroll quando necessario.
// Em $cco vira lista flat sem gap/padding (rows com border-bottom
// hairline criam o divisor).
// Estado vazio do painel (sem alertas nem atividade) — so no layout
// $cco, pra a coluna fixa nao ficar preta. Tom calmo: check verde +
// mensagem centralizada ("nada pra ver = bom sinal"). Ocupa o flex:1
// (mesmo espaco que a lista ocuparia).
export const AlertEmpty = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 32px var(--cco-pad);
  text-align: center;

  svg {
    color: var(--cco-green);
    opacity: 0.85;
    margin-bottom: 4px;
  }
  .title {
    font-size: 13px;
    font-weight: 700;
    color: var(--cco-ink);
  }
  .sub {
    font-size: 12px;
    font-weight: 500;
    color: var(--cco-ink-faint);
    max-width: 220px;
    line-height: 1.4;
  }
`;

// Rodape do painel — acesso ao historico persistente. flex-shrink:0 pra
// ficar fixo fora do scroll da lista. Link discreto + botao limpar.
export const AlertFooter = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: ${(props) => (props.$cco ? '10px var(--cco-pad)' : '8px 10px')};
  border-top: 1px solid var(--cco-line);

  .link {
    background: transparent;
    border: none;
    cursor: pointer;
    font-family: inherit;
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 0.2px;
    color: var(--cco-blue);
    padding: 4px 2px;
  }
  .link:hover {
    text-decoration: underline;
  }
  .clear {
    background: transparent;
    border: none;
    cursor: pointer;
    font-family: inherit;
    font-size: 11px;
    font-weight: 600;
    color: var(--cco-ink-faint);
    padding: 4px 2px;
  }
  .clear:hover {
    color: var(--cco-red);
  }
`;

export const AlertList = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;

  /* Scroll discreto (Firefox + WebKit): thumb fino em tom de linha,
     track transparente — some no fundo escuro do CCO e so aparece
     quando ha overflow real. Mantem a altura do card estavel. */
  scrollbar-width: thin;
  scrollbar-color: var(--cco-line-strong) transparent;
  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: var(--cco-line-strong);
    border-radius: 3px;
  }
  &::-webkit-scrollbar-thumb:hover {
    background: var(--cco-ink-faint);
  }

  ${(props) =>
    props.$cco
      ? `
        gap: 0;
        padding: 0;
      `
      : `
        gap: 4px;
        padding-right: 2px;
      `}
`;

// Em $cco: header com padding generoso, border-bottom hairline,
// titulo (red) a esquerda + count (mute) a direita.
export const AlertHeader = styled.div`
  flex-shrink: 0;
  ${(props) =>
    props.$cco
      ? `
        padding: 18px var(--cco-pad) 14px;
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 8px;
        border-bottom: 1px solid var(--cco-line);

        > .lbl {
          display: flex;
          align-items: center;
          gap: 10px;
          font-size: 11px;
          letter-spacing: 0.14em;
          text-transform: uppercase;
          font-weight: 600;
          color: ${
            props.$severity === 'attention'
              ? 'var(--cco-orange)'
              : props.$severity === 'activity'
              ? 'var(--cco-blue)'
              : props.$severity === 'ok'
              ? 'var(--cco-green)'
              : 'var(--cco-red)'
          };
        }
        > .ct {
          color: var(--cco-ink-mute);
          font-size: 12px;
          font-weight: 500;
        }
      `
      : `
        display: flex;
        align-items: center;
        gap: 6px;
        font-size: 11px;
        font-weight: 800;
        text-transform: uppercase;
        letter-spacing: 0.3px;
        color: ${
          props.$severity === 'attention'
            ? props.theme.colors.orange
            : props.$severity === 'activity'
            ? props.theme.colors.blue
            : props.$severity === 'ok'
            ? props.theme.colors.watergreen
            : props.theme.colors.red
        };
      `}
`;

export const AlertHeaderDot = styled.span`
  width: ${(props) => (props.$cco ? '7px' : '8px')};
  height: ${(props) => (props.$cco ? '7px' : '8px')};
  border-radius: 50%;
  background: ${(props) => {
    const attn = props.$severity === 'attention';
    const act = props.$severity === 'activity';
    const ok = props.$severity === 'ok';
    if (props.$cco)
      return attn
        ? 'var(--cco-orange)'
        : act
        ? 'var(--cco-blue)'
        : ok
        ? 'var(--cco-green)'
        : 'var(--cco-red)';
    return attn
      ? props.theme.colors.orange
      : act
      ? props.theme.colors.blue
      : ok
      ? props.theme.colors.watergreen
      : props.theme.colors.red;
  }};
  ${(props) =>
    props.$cco
      ? `
        box-shadow: 0 0 0 3px ${
          props.$severity === 'attention'
            ? 'rgba(246, 154, 96, 0.2)'
            : props.$severity === 'activity'
            ? 'rgba(56, 132, 255, 0.2)'
            : props.$severity === 'ok'
            ? 'rgba(99, 204, 166, 0.2)'
            : 'rgba(244, 68, 92, 0.2)'
        };
      `
      : `
        animation: alert-pulse 1.5s ease-in-out infinite;
        @keyframes alert-pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.4; }
        }
      `}
`;

// Botao 'Ack' (visto) por item do AlertOverlay no modo $cco. Aparece
// so no hover do AlertItem (opacity 0 default), ao lado do chevron.
// Click marca o alerta como visto por 5min — esconde da lista sem
// afetar a logica de alerta no backend ou no marker do mapa. Util
// pra reduzir ruido visual em fila de muitos alertas.
export const AlertItemAck = styled.button`
  flex-shrink: 0;
  width: 24px;
  height: 24px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-left: auto;
  margin-right: 4px;
  border: 1px solid var(--cco-line);
  background: transparent;
  border-radius: 4px;
  color: var(--cco-ink-mute);
  cursor: pointer;
  opacity: 0;
  pointer-events: none;
  transition: opacity 150ms, color 150ms, border-color 150ms, background 150ms;

  /* Aparece no hover do parent (AlertItem). Mantem clickable so
     quando visivel pra nao competir com o click do AlertItem. */
  button[type='button']:hover > &,
  ${/* sc-selector */ ''}
  *:hover > & {
    opacity: 1;
    pointer-events: auto;
  }

  /* Visivel direto em touch devices que nao tem hover */
  @media (hover: none) {
    opacity: 1;
    pointer-events: auto;
  }

  &:hover {
    color: var(--cco-green, #2ecc71);
    border-color: var(--cco-green, #2ecc71);
    background: rgba(46, 204, 113, 0.08);
  }
`;

// Hint discreto na sidebar de estados ensinando a interacao de
// hover-pan. Aparece so 1x por instalacao (persiste em localStorage
// via 'cco-hover-pan-hint-seen'). Estilo info-toast com botao de
// dismiss explicito. Some no primeiro hover OU click no X.
export const HoverPanHint = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 8px var(--cco-pad);
  padding: 8px 10px;
  border-radius: 6px;
  background: rgba(59, 130, 246, 0.08);
  border: 1px solid rgba(59, 130, 246, 0.22);
  color: var(--cco-ink-mute);
  font-size: 11.5px;
  font-weight: 500;
  line-height: 1.35;

  > .text {
    flex: 1;
  }
  > button {
    flex-shrink: 0;
    width: 22px;
    height: 22px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: transparent;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    color: var(--cco-ink-faint);
    transition: color 150ms, background 150ms;
    &:hover {
      color: var(--cco-ink);
      background: rgba(255, 255, 255, 0.05);
    }
  }
`;

// Divisor entre o grupo de alarmes e o de atencao na overlay. Em $cco
// aparece como subhead discreto com hairline em volta; em legado, so
// uma chip pequena. Reforca visualmente a mudanca de severidade pra
// operador nao confundir o boundary.
export const AlertGroupDivider = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 10.5px;
  font-weight: 800;
  letter-spacing: 0.5px;
  text-transform: uppercase;

  ${(props) =>
    props.$cco
      ? `
        color: ${
          props.$severity === 'alarm'
            ? 'var(--cco-red)'
            : props.$severity === 'activity'
            ? 'var(--cco-ink-mute)'
            : 'var(--cco-yellow)'
        };
        padding: 10px var(--cco-pad) 6px;
        background: transparent;

        &::before {
          content: '';
          width: 12px;
          height: 1px;
          background: var(--cco-line);
        }
        &::after {
          content: '';
          flex: 1;
          height: 1px;
          background: var(--cco-line);
        }
      `
      : `
        color: ${
          props.$severity === 'alarm'
            ? props.theme.colors.red
            : props.theme.colors.orange
        };
        padding: 6px 4px 2px;
      `}
`;

export const AlertItem = styled.button`
  display: flex;
  align-items: center;
  cursor: pointer;
  text-align: left;
  width: 100%;
  transition: background 120ms;
  min-width: 0;

  ${(props) =>
    props.$cco
      ? `
        gap: 14px;
        padding: 16px var(--cco-pad);
        /* relative: ancora do ItemTimeTag (tempo no canto sup. direito —
           mesma posicao em alertas e atividades, homogeneiza a lista). */
        position: relative;
        background: ${
          props.$old
            ? 'rgba(232, 65, 65, 0.06)'
            : 'transparent'
        };
        border: none;
        border-bottom: 1px solid var(--cco-line);
        /* Stripe lateral de severidade: vermelho (alarme/critico) ou
           laranja (atencao) — triagem visual instantanea por item. */
        border-left: 3px solid ${
          props.$severity === 'attention'
            ? 'var(--cco-orange)'
            : 'var(--cco-red)'
        };
        padding-left: calc(var(--cco-pad) - 3px);
        border-radius: 0;
        color: var(--cco-ink);
        font-size: 14px;

        &:last-child {
          border-bottom: none;
        }

        > .chev {
          margin-left: auto;
          color: var(--cco-ink-faint);
          transition: color 150ms, transform 150ms;
          flex-shrink: 0;
          display: inline-flex;
        }

        &:hover {
          background: ${
            props.$old
              ? 'rgba(232, 65, 65, 0.10)'
              : 'rgba(255, 255, 255, 0.025)'
          };
        }
        &:hover > .chev {
          color: var(--cco-ink-mute);
          transform: translateX(2px);
        }

        /* Realce quando o marker correspondente esta sob hover no mapa
           (link mapa->lista). Mesma cor do hover do item. */
        ${
          props.$hovered
            ? `background: ${
                props.$old
                  ? 'rgba(232, 65, 65, 0.12)'
                  : 'rgba(255, 255, 255, 0.06)'
              };`
            : ''
        }
      `
      : `
        gap: 8px;
        padding: 8px 10px;
        background: transparent;
        border: 1px solid ${props.theme.colors.disabled};
        border-radius: 6px;
        color: ${props.theme.colors.darkerblue};
        font-size: 12px;

        ::after {
          content: '›';
          margin-left: auto;
          font-size: 18px;
          line-height: 1;
          color: ${props.theme.colors.label};
          transition: color 150ms, transform 150ms;
        }

        :hover {
          background: ${
            props.theme.title === 'light'
              ? props.theme.colors.disabled
              : 'rgba(255, 255, 255, 0.04)'
          };
          border-color: ${props.theme.colors.red};
        }
        :hover::after {
          color: ${props.theme.colors.red};
          transform: translateX(2px);
        }

        ${
          props.$fresh
            ? `
              border-color: ${props.theme.colors.red};
              animation: alert-item-pulse 1.2s ease-in-out infinite;
              @keyframes alert-item-pulse {
                0%, 100% { box-shadow: 0 0 0 0 ${props.theme.colors.red}; }
                50% { box-shadow: none; }
              }
            `
            : ''
        }
      `}
`;

// Wrapper interno do item de alerta — empilha nome + cliente em duas
// linhas. flex: 1 + min-width: 0 permite truncar.
export const AlertItemContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1px;
  flex: 1;
  min-width: 0;
`;

// Nome do cliente (subtitulo do nome do parque). Tipografia menor +
// cor label pra hierarquia. Trunca com ellipsis se passar da largura
// disponivel.
export const AlertItemCustomer = styled.span`
  font-size: ${(props) => (props.$cco ? '12.5px' : '10px')};
  font-weight: ${(props) => (props.$cco ? '500' : '600')};
  color: ${(props) =>
    props.$cco ? 'var(--cco-ink-mute)' : props.theme.colors.label};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  ${(props) => props.$cco && 'margin-top: 2px;'}
`;

// Nome da MAQUINA (thing) que disparou o alarme — ex.: "UCHA-01". Fica
// entre o cliente e a causa pra o operador identificar de imediato qual
// equipamento da praca esta em alerta. Cor de tinta forte (vs cliente
// mais apagado) + icone de equipamento.
export const AlertItemMachine = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: ${(props) => (props.$cco ? '12px' : '10px')};
  font-weight: 700;
  color: ${(props) =>
    props.$cco ? 'var(--cco-ink)' : props.theme.colors.darkerblue};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  margin-top: 3px;

  > svg {
    flex-shrink: 0;
    opacity: 0.7;
  }
`;

// Dot do item de alerta. Cor vem direto do getMarkerFillColor (mesma do
// marker no mapa) para consistencia visual. Quando attention=true,
// adiciona um anel amarelo externo (box-shadow) igual ao MARKER_OUTLINE
// do marker do mapa — destaca visualmente os parques em atencao.
export const AlertItemDot = styled.span`
  width: ${(props) => (props.$cco ? '8px' : '10px')};
  height: ${(props) => (props.$cco ? '8px' : '10px')};
  border-radius: 50%;
  background: ${(props) => props.color};
  flex-shrink: 0;
  ${(props) =>
    props.attention &&
    `
      box-shadow: 0 0 0 2px ${
        props.$cco ? 'var(--cco-yellow)' : props.theme.colors.yellow
      };
      margin: 2px;
    `}
`;

export const AlertItemName = styled.span`
  font-size: ${(props) => (props.$cco ? '14px' : '12px')};
  font-weight: ${(props) => (props.$cco ? '600' : '700')};
  color: ${(props) => (props.$cco ? 'var(--cco-ink)' : 'inherit')};
  /* Reserva o canto sup. direito pro ItemTimeTag (absoluto) nao cobrir
     o nome em titulos longos. */
  ${(props) => props.$cco && 'padding-right: 48px;'}
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  /* Permite que o pill de duracao (AlertItemDuration) flutue na direita,
     mas mantem ellipsis no nome longo. Alvo explicito .nm: o nome agora
     vem embrulhado num <span class="nm"> — antes o seletor :first-child
     pegava o proprio pill de duracao (primeiro ELEMENTO filho, ja que o
     nome era text node puro) e o flex:1 esticava o pill pela largura toda. */
  display: flex;
  align-items: center;
  gap: 8px;
  .nm {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

// Pill de duracao do alarme. Vermelho intenso pra alarmes antigos
// (>15min — flag SLA $old) ou vermelho discreto pra alarmes recentes.
// Operador identifica em 1s o park que merece intervencao prioritaria.
export const AlertItemDuration = styled.span`
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  padding: 1px 6px;
  border-radius: 9px;
  font-size: 10.5px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  letter-spacing: -0.1px;
  background: ${(props) =>
    props.$old ? 'var(--cco-red)' : 'rgba(232, 65, 65, 0.16)'};
  color: ${(props) =>
    props.$old ? 'var(--cco-bg, #0f1117)' : 'var(--cco-red)'};

  /* SLA visivel: alarme antigo pulsa pra atrair olho do operador */
  ${(props) =>
    props.$old &&
    `
      animation: duration-old-pulse 1.6s ease-in-out infinite;
      @keyframes duration-old-pulse {
        0%, 100% { opacity: 1; }
        50% { opacity: 0.65; }
      }
    `}
`;

export const AlertItemBadge = styled.span`
  font-size: 10px;
  padding: 1px 6px;
  border-radius: 6px;
  background: ${(props) => props.theme.colors.red};
  color: ${(props) => props.theme.colors.white};
  font-weight: 800;
  flex-shrink: 0;
`;

// === Zona ATIVIDADE (notificacoes transitorias de transicao por-maquina) ===
// Visual mais leve que os AlertItem: linha discreta com dot colorido pela
// transicao + nome da maquina/verbo + parque + tempo relativo. Auto-expiram
// no hook; aqui so estilo. Fade-in sutil na entrada pra dar sensacao de
// "notificacao que chegou" sem ser distrativo.
export const ActivityItem = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  min-width: 0;
  /* border-box: sem isso o padding lateral somava aos 100% e o item
     ficava mais largo que o container — o tempo (a direita) era clipado
     pelo overflow-x:hidden do AlertList. */
  box-sizing: border-box;
  animation: activity-in 220ms ease-out;

  ${(props) =>
    props.$cco
      ? `
        /* Mesma estrutura visual do AlertItem: separador embaixo +
           faixa lateral COLORIDA (a cor e o unico diferenciador, sem
           rotulo de secao). padding-left compensa os 3px da faixa. */
        position: relative;
        /* 16px vertical: MESMO respiro do AlertItem — alertas e atividades
           com a mesma regua de altura/tipografia (lista homogenea). */
        padding: 16px var(--cco-pad);
        border-bottom: 1px solid var(--cco-line);
        border-left: 3px solid ${props.$color || 'var(--cco-line-strong)'};
        padding-left: calc(var(--cco-pad) - 3px);
        &:last-child { border-bottom: none; }
      `
      : `
        padding: 8px 4px;
        border-top: 1px solid var(--cco-line);
      `}

  @keyframes activity-in {
    from {
      opacity: 0;
      transform: translateY(-3px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

export const ActivityDot = styled.span`
  flex-shrink: 0;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${(props) => props.$color || 'var(--cco-ink-faint)'};
`;

export const ActivityBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  flex: 1;

  /* Mesma regua tipografica do AlertItem: titulo 14 (maquina em 700,
     verbo em 500) e contexto 12.5 ink-mute — lista homogenea. O
     padding-right reserva o canto do ItemTimeTag (absoluto). */
  .evt {
    font-size: 14px;
    font-weight: 500;
    color: var(--cco-ink);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    padding-right: 48px;
  }
  .evt .nm {
    font-weight: 700;
  }
  .park {
    font-size: 12.5px;
    font-weight: 500;
    color: var(--cco-ink-mute);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

export const ActivityTime = styled.span`
  flex-shrink: 0;
  font-size: 11px;
  font-weight: 600;
  color: var(--cco-ink-faint);
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
`;

// Tempo no canto SUPERIOR DIREITO do item — mesma posicao em alertas e
// atividades (homogeneiza a lista; antes alertas tinham pill inline no
// titulo + "ha X" dentro da causa, e atividades tempo a direita centrado).
// $sla: vira pill vermelho de tempo-em-alarme; $old (>15min) pulsa (SLA).
export const ItemTimeTag = styled.span`
  position: absolute;
  top: 14px;
  right: var(--cco-pad);
  z-index: 1;
  font-size: 11px;
  font-weight: 600;
  color: var(--cco-ink-faint);
  font-variant-numeric: tabular-nums;
  white-space: nowrap;

  ${(props) =>
    props.$sla &&
    `
      padding: 1px 7px;
      border-radius: 9px;
      font-size: 10.5px;
      font-weight: 700;
      background: ${props.$old ? 'var(--cco-red)' : 'rgba(232, 65, 65, 0.16)'};
      color: ${props.$old ? 'var(--cco-bg, #0f1117)' : 'var(--cco-red)'};
    `}

  ${(props) =>
    props.$sla &&
    props.$old &&
    `
      animation: item-time-old-pulse 1.6s ease-in-out infinite;
      @keyframes item-time-old-pulse {
        0%, 100% { opacity: 1; }
        50% { opacity: 0.65; }
      }
    `}
`;

// Linha de causa do alerta no AlertItem. Aparece SE o backend mandar
// topAlarm preenchido. Visual compacto.
//
// Layout:
//   [icon]  Variavel Valor Limite · ha X min
//           (em ate 2 linhas com ellipsis se passar)
//
// Range: "↑ Pressão Saída 45.2 bar (>40) · há 3 min"
// Firmware: "⚠ ALARME - BOTÃO DE EMERGÊNCIA FOI PRESSIONADO · há 2 min"
//          (frases longas do firmware quebram em 2 linhas)
//
// Cor segue severidade ($severity = 'error' | 'warning').
export const AlertItemCause = styled.span`
  display: flex;
  align-items: flex-start;
  gap: 5px;
  font-size: ${(props) => (props.$cco ? '11.5px' : '10px')};
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  line-height: 1.35;
  color: ${(props) => {
    if (props.$severity === 'error') {
      return props.$cco ? 'var(--cco-red)' : props.theme.colors.red;
    }
    if (props.$severity === 'warning') {
      return props.$cco ? 'var(--cco-yellow)' : props.theme.colors.orange;
    }
    return props.$cco ? 'var(--cco-ink-mute)' : props.theme.colors.label;
  }};
  ${(props) => props.$cco && 'margin-top: 3px;'}

  /* Icon fica fixo na esquerda alinhado com a primeira linha de texto */
  > svg {
    flex-shrink: 0;
    margin-top: 2px;
  }

  /* Container que limita o texto a 2 linhas com ellipsis. line-clamp +
     -webkit-box e a unica forma cross-browser de truncar em N linhas. */
  > .text {
    flex: 1;
    min-width: 0;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    word-break: break-word;
  }

  > .text > .var {
    font-weight: 700;
  }
  > .text > .val {
    font-weight: 800;
    margin-left: 4px;
  }
  > .text > .lim {
    color: ${(props) =>
      props.$cco ? 'var(--cco-ink-faint)' : props.theme.colors.label};
    font-weight: 600;
    margin-left: 4px;
  }
  > .text > .sep {
    color: ${(props) =>
      props.$cco ? 'var(--cco-ink-faint)' : props.theme.colors.label};
    font-weight: 600;
    margin: 0 4px;
  }
  > .text > .ago {
    color: ${(props) =>
      props.$cco ? 'var(--cco-ink-faint)' : props.theme.colors.label};
    font-weight: 500;
  }
`;

// Card destacado na sidebar de detalhe (MarkerDetailPanel) mostrando a
// causa principal do alarme. Visual: borda + tint na cor de severidade
// pra atrair olho do operador antes das outras sections (Tempo real,
// Equipamentos). Distinto do MetricRow comum.
export const CauseCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 8px 10px;
  margin: 4px 6px 0;
  border-radius: 8px;
  border: 1px solid
    ${(props) => {
      if (props.$severity === 'error') return props.theme.colors.red;
      if (props.$severity === 'warning') return props.theme.colors.orange;
      return props.theme.colors.disabled;
    }};
  background: ${(props) => {
    const isLight = props.theme.title === 'light';
    if (props.$severity === 'error') {
      return isLight ? 'rgba(232, 65, 65, 0.06)' : 'rgba(232, 65, 65, 0.10)';
    }
    if (props.$severity === 'warning') {
      return isLight
        ? 'rgba(245, 158, 11, 0.06)'
        : 'rgba(245, 158, 11, 0.10)';
    }
    return 'transparent';
  }};
  font-variant-numeric: tabular-nums;
`;

// Linha principal do CauseCard: seta + nome da variavel + valor.
export const CauseHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 800;
  color: ${(props) => {
    if (props.$severity === 'error') return props.theme.colors.red;
    if (props.$severity === 'warning') return props.theme.colors.orange;
    return props.theme.colors.darkerblue;
  }};

  > .variable {
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  > .value {
    font-weight: 800;
  }
`;

// Linha secundaria do CauseCard: appliance + limite + tempo.
export const CauseMeta = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 6px;
  font-size: 11px;
  font-weight: 600;
  color: ${(props) => props.theme.colors.label};

  > .threshold {
    font-weight: 700;
  }
  > .ago {
    margin-left: auto;
    font-weight: 500;
  }
`;

export const ErrorMapContainer = styled.div`
  width: 100%;
  height: 100%;
  flex: 1;
  display: flex;
  flex-direction: column;
  /* No /dashboard a sidebar de zonas/clientes ocupa 267px a esquerda;
     o width:calc deixa espaco pra ela em viewports >=1589px. No modo
     embedded ou $cco (grid 3-col controla a largura) a regra e
     prejudicial — sobra espaco preto a direita ou colapsa o mapa. */
  @media (min-width: 1589px) {
    width: ${(props) =>
      props.$embedded || props.$cco ? '100%' : 'calc(100% - 267px)'};
  }

  /* Defensivo: em embedded/$cco, alguns navegadores resolviam flex-basis:0
     antes do width:100%, deixando o filho menor que o parent. Forcamos
     min/max pra garantir 100% real. */
  ${(props) =>
    (props.$embedded || props.$cco) &&
    `
      min-width: 100%;
      max-width: 100%;
    `}

  /* Em mobile o grid pai define grid-template-rows com altura definida
     pro mapa. Garantimos min-height pra o Google Maps medir corretamente
     ao montar (sem isso ele as vezes pinta 0px). */
  @media (max-width: 720px) {
    min-height: 50vh;
  }
`;

export const FilterContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  row-gap: 8px;
  width: ${(props) =>
    props.margin ? 'calc(100% - 50px)' : 'calc(100% - 100px)'};
  margin: 10px 0 25px 0;
  align-items: center;
  justify-content: center;
  @media (max-width: 1300px) {
    justify-content: center;
    margin: 10px 0 25px 0;
    width: ${(props) => props.width};
  }
`;

export const Filter = styled.div`
  display: flex;
  width: 150px;
  ${(props) =>
    props.noneSelected &&
    `background: ${props.color}; color: ${props.theme.colors.white}`}
  background: ${(props) =>
    props.selected ? props.color : props.theme.colors.card};
  color: ${(props) =>
    props.selected || props.noneSelected
      ? props.theme.colors.white
      : props.color};
  border: ${(props) => `1px solid ${props.color}`};
  border-radius: 10px;
  text-align: center;
  align-items: center;
  justify-content: center;
  margin: 0 10px;
  padding: 3px;
  cursor: pointer;
  transition: background-color 0.5s;

  label {
    font-size: 12px;
    font-weight: bold;
    text-transform: uppercase;
    cursor: pointer;
  }

  @media (min-width: 1301px) {
    margin: 0 25px;
  }
`;

export const MapContainer = styled.div`
  position: relative;
  width: 100%;
  flex: 1;
  /* Mapa flush com as bordas do card no topo, direita e baixo — sem
     margem que deixava barras escuras. Border-radius zerado pra cantos
     do mapa casarem com a borda do container. */
  margin: 0;
  border-radius: 0;

  .gm-style-iw.gm-style-iw-c {
    width: 240px !important;
    padding: 10px 14px !important;
    max-height: unset !important;
    border-radius: 6px !important;

    .gm-style-iw-d {
      max-height: unset !important;
    }
  }

  .gm-style-iw-t::after {
    z-index: unset;
  }

  /* Modo TV: quando em fullscreen, o container ocupa a tela toda do
     monitor. Background da pagina pra evitar barra branca antes do
     mapa pintar. */
  &:fullscreen {
    width: 100vw;
    height: 100vh;
    background: ${(props) => props.theme.colors.background};
  }
  &:-webkit-full-screen {
    width: 100vw;
    height: 100vh;
    background: ${(props) => props.theme.colors.background};
  }
`;

export const MarkerContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  width: 100%;
  padding: 2px 0;
`;

// Header da InfoWindow: linha com StatusBadge e ErrorBadge lado-a-lado.
export const MarkerHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  flex-wrap: wrap;
  margin-bottom: 8px;
`;

// ============================================================================
// InfoWindow do marker — redesenhado com hierarquia visual clara
// ============================================================================

// Pill de status grande no topo do popup — cor do marker (vermelho/laranja/
// verde/cinza) + dot + label. Substitui o antigo Status que tinha um sub-div
// para errorCount ao lado, agora separado em ErrorBadge.
export const StatusBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 12px;
  border-radius: 8px;
  background: ${({ background, theme }) => background || theme.colors.card};
  color: ${({ theme }) => theme.colors.white};
  font-size: 11px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.4px;

  &::before {
    content: '';
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: currentColor;
    opacity: 0.85;
  }
`;

// Badge separada de erro quando status.E > 0 (so aparece em alarme/atencao
// com contagem de erros).
export const ErrorBadge = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 24px;
  height: 22px;
  padding: 0 7px;
  border-radius: 6px;
  background: ${({ theme }) => theme.colors.red};
  color: ${({ theme }) => theme.colors.white};
  font-size: 11px;
  font-weight: 800;
  font-variant-numeric: tabular-nums;
`;

export const Status = StatusBadge; // alias para retrocompat

export const Separator = styled.div`
  width: 100%;
  height: 1px;
  background: ${(props) => props.theme.colors.disabled};
  margin: 10px 0;
`;

// Titulo do parque (nome do marker). Tipografia destacada como heading.
export const ParkTitle = styled.h3`
  margin: 0;
  font-size: 15px;
  font-weight: 800;
  color: ${(props) => props.theme.colors.darkerblue};
  text-align: center;
  word-break: break-word;
  line-height: 1.2;
`;

// Subtitulo com nome do cliente + icone. Menor e cor label pra hierarquia.
export const CustomerSubtitle = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  margin-top: 4px;
  font-size: 11px;
  font-weight: 600;
  color: ${(props) => props.theme.colors.label};

  img {
    height: 11px;
    width: 11px;
    object-fit: contain;
    opacity: 0.7;
  }
`;

// Linha de chips informativos (tipo + equipamentos count). Substitui o
// TypeTag + ApplianceAmount separados, mais limpo lado a lado.
export const ChipRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 6px;
  margin: 10px 0 4px;
`;

export const Chip = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 4px 9px;
  border-radius: 6px;
  background: ${(props) =>
    props.theme.title === 'light'
      ? props.theme.colors.surfaceMuted
      : 'rgba(255, 255, 255, 0.06)'};
  font-size: 11px;
  font-weight: 700;
  color: ${(props) => props.theme.colors.darkerblue};
  text-transform: uppercase;
  letter-spacing: 0.3px;
  font-variant-numeric: tabular-nums;

  /* Dot colorido para tipo (ucha=laranja, bomba=azul) */
  > span.dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: ${(props) => props.dotColor || props.theme.colors.orange};
    flex-shrink: 0;
  }

  strong {
    color: ${(props) => props.theme.colors.darkblue};
    font-weight: 800;
    font-size: 13px;
  }
`;

// Aliases retrocompativeis com o antigo TypeTag/Customer/Park/ApplianceAmount.
// O JSX foi reescrito; mantidos pra nao quebrar imports externos.
export const TypeTag = Chip;
export const Customer = CustomerSubtitle;
export const Park = ParkTitle;
export const ApplianceAmount = ChipRow;

// Botao CTA principal "Ver detalhes". Agora full-width, accent color
// (lightblue), com chevron animado no hover.
export const SeeMore = styled.button`
  /* Sem margin-top: o gap do parent MarkerDetailPanel ja separa do
     DetailContent. flex-shrink: 0 garante que o botao mantem altura
     mesmo se o painel for muito comprimido (improvavel mas defensivo). */
  flex-shrink: 0;
  width: 100%;
  padding: 8px 14px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  border-radius: 6px;
  border: 1px solid ${(props) => props.theme.colors.lightblue};
  background: ${(props) => props.theme.colors.lightblue};
  color: ${(props) => props.theme.colors.white};
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  cursor: pointer;
  transition: all 180ms;

  &::after {
    content: '›';
    font-size: 18px;
    line-height: 1;
    transition: transform 180ms;
  }

  :hover {
    background: ${(props) => props.theme.colors.blue};
    border-color: ${(props) => props.theme.colors.blue};
    transform: translateY(-1px);

    &::after {
      transform: translateX(3px);
    }
  }

  :focus-visible {
    outline: 2px solid ${(props) => props.theme.colors.lightblue};
    outline-offset: 2px;
  }
`;

export const MapLegend = styled.div`
  position: absolute;
  bottom: 12px;
  left: 12px;
  z-index: 5;
  display: grid;
  grid-template-columns: 1fr 1fr;
  column-gap: 16px;
  row-gap: 6px;
  padding: 10px 14px;
  background: ${(props) => props.theme.colors.card};
  border: 1px solid ${(props) => props.theme.colors.disabled};
  border-radius: 6px;
  box-shadow: ${overlayShadow};
  font-size: 12px;
`;

export const MapLegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  color: ${(props) => props.theme.colors.darkerblue};
  /* Quando interativo (botao), padding + cursor + transitions */
  ${(props) =>
    props.$interactive &&
    `
      padding: 2px 4px;
      margin: -2px -4px;
      border: 1px solid transparent;
      border-radius: 6px;
      background: transparent;
      font-size: 12px;
      cursor: pointer;
      transition: opacity 120ms, border-color 120ms;
      opacity: ${props.$active ? 1 : 0.5};
      text-align: left;
      &:hover {
        opacity: 1;
        border-color: ${props.theme.colors.lightblue};
      }
    `}
`;

export const MapLegendDot = styled.span`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: ${(props) => (props.$hollow ? 'transparent' : props.color)};
  ${(props) =>
    props.$hollow &&
    `box-shadow: inset 0 0 0 1.5px ${props.color};`}
  flex-shrink: 0;
`;

// Contagem de maquinas por status ao lado da label — pill discreto, fica
// no fim do item (margin-left auto empurra pra direita).
export const MapLegendCount = styled.span`
  margin-left: auto;
  padding: 0 6px;
  min-width: 18px;
  text-align: center;
  border-radius: 999px;
  background: var(--cco-soft-surface);
  border: 1px solid var(--cco-line);
  font-size: 10.5px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  color: var(--cco-ink-mute);
`;

// Barra de chips de filtro de status, top-center do mapa. Cada chip
// representa um status (alarme/atencao/rodando/desligada/sem dados),
// click toggla a inclusao no filterStatus. Quando todos ativos, todos
// markers aparecem; click pra desativar um -> markers desse status
// somem do mapa. Util pra operador focar em alertas.
export const FilterChipsBar = styled.div`
  position: absolute;
  top: 12px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 5;
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 6px;
  background: ${(props) => props.theme.colors.card};
  border: 1px solid ${(props) => props.theme.colors.disabled};
  border-radius: 8px;
  box-shadow: ${overlayShadow};

  @media (max-width: 1400px) {
    /* Em viewport apertado, deixa quebrar em duas linhas em vez de
       sobrepor com os controles top-left/top-right. */
    flex-wrap: wrap;
    max-width: calc(100% - 480px);
    justify-content: center;
  }
`;

export const FilterChip = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 4px 9px;
  border-radius: 6px;
  background: ${(props) =>
    props.$active
      ? props.theme.title === 'light'
        ? props.theme.colors.disabled
        : 'rgba(255, 255, 255, 0.06)'
      : 'transparent'};
  border: 1px solid
    ${(props) =>
      props.$active ? props.color : props.theme.colors.disabled};
  color: ${(props) => props.theme.colors.darkerblue};
  font-size: 11px;
  font-weight: 700;
  text-transform: capitalize;
  cursor: pointer;
  transition: opacity 150ms, border-color 150ms, background 150ms;
  opacity: ${(props) => (props.$active ? 1 : 0.45)};
  white-space: nowrap;

  > .dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: ${(props) => props.color};
    flex-shrink: 0;
    /* Quando desativado, dot fica transparente outline only */
    ${(props) =>
      !props.$active &&
      `
        background: transparent;
        box-shadow: inset 0 0 0 1.5px ${props.color};
      `}
  }

  &:hover {
    opacity: 1;
  }
`;

// Indicador discreto "Atualizado ha Xs" ao lado do botao Tela cheia.
// Mostra ao operador na TV que o dado e fresco, nao congelado por
// erro de rede. Visual menor que o botao pra nao competir visualmente.
// Quando $failed (subscription GraphQL caiu), ganha borda vermelha
// pra alertar.
export const UpdatedAgo = styled.div`
  position: absolute;
  top: 18px;
  left: 56px;
  z-index: 5;
  /* Em tela cheia, sai de baixo do dock "Top Geracao" (esq) — desloca pra
     direita dele. */
  ${(props) =>
    props.$fs && `left: calc(min(320px, 28vw) + 36px);`}
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  background: ${(props) => props.theme.colors.card};
  border: 1px solid
    ${(props) =>
      props.$failed
        ? props.theme.colors.red
        : props.theme.colors.disabled};
  border-radius: 6px;
  box-shadow: ${overlayShadow};
  color: ${(props) =>
    props.$failed
      ? props.theme.colors.red
      : props.theme.colors.darkerblue};
  font-size: 11px;
  pointer-events: none;
  font-weight: ${(props) => (props.$failed ? 700 : 400)};
`;

// Banner proeminente quando a subscription de tempo real cai por mais
// de alguns segundos. O UpdatedAgo (pill discreto no canto) ja muda
// pra "Reconectando…", mas num CCO em TV grande isso passa
// despercebido — e dado em tempo real congelado e perigoso pro
// operador (decide com numero velho achando que e live). Banner
// centralizado no topo do mapa, vermelho, impossivel de ignorar.
export const SubscriptionBanner = styled.div`
  position: absolute;
  top: 16px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 7;
  display: flex;
  align-items: center;
  gap: 12px;
  max-width: calc(100% - 96px);
  padding: 10px 14px;
  background: ${(props) => props.theme.colors.red};
  color: #fff;
  border-radius: 8px;
  box-shadow: ${overlayShadow};
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.01em;

  > svg {
    width: 18px;
    height: 18px;
    stroke: currentColor;
    stroke-width: 2;
    fill: none;
    stroke-linecap: round;
    stroke-linejoin: round;
    flex-shrink: 0;
  }

  > .msg {
    display: flex;
    flex-direction: column;
    line-height: 1.25;
  }
  > .msg .sub {
    font-size: 11px;
    font-weight: 500;
    opacity: 0.92;
  }

  @media (max-width: 720px) {
    font-size: 12px;
    padding: 8px 10px;
    gap: 8px;
  }
`;

export const SubscriptionReloadBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background: rgba(255, 255, 255, 0.18);
  color: #fff;
  border: 1px solid rgba(255, 255, 255, 0.45);
  border-radius: 999px;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  transition: background 0.15s;
  flex-shrink: 0;
  font-family: inherit;

  > svg {
    width: 14px;
    height: 14px;
    stroke: currentColor;
    stroke-width: 2.2;
    fill: none;
    stroke-linecap: round;
    stroke-linejoin: round;
  }

  &:hover {
    background: rgba(255, 255, 255, 0.3);
  }
  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 3px rgba(255, 255, 255, 0.4);
  }
`;

// Overlay centralizado sobre o mapa quando nao ha parques pra mostrar
// (sem dados OU filtro escondeu todos). Sem isso o operador ve um mapa
// vazio do Brasil sem saber se carregou e nao ha nada, se o filtro
// cortou tudo, ou se deu erro silencioso.
export const MapEmptyOverlay = styled.div`
  position: absolute;
  inset: 0;
  z-index: 6;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  pointer-events: none;

  /* So o card interno captura clique (botao de limpar filtro). O
     restante deixa passar pra nao bloquear pan/zoom do mapa atras. */
  > * {
    pointer-events: auto;
  }
`;

// Card de fundo pro EmptyState — sem isso o texto fica ilegivel sobre
// as tiles do mapa. Surface solido com borda + sombra, centralizado.
export const MapEmptyCard = styled.div`
  background: ${(props) => props.theme.colors.card};
  border: 1px solid ${(props) => props.theme.colors.disabled};
  border-radius: 12px;
  box-shadow: ${overlayShadow};
  max-width: 420px;
`;

// Botao do empty state ("Mostrar todos") — primary blue, distinto do
// botao branco-sobre-vermelho do SubscriptionBanner.
export const EmptyFilterBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  background: ${(props) => props.theme.colors.blue};
  color: #fff;
  border: none;
  border-radius: 999px;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  transition: filter 0.15s;
  font-family: inherit;

  &:hover {
    filter: brightness(1.08);
  }
  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 3px ${(props) => props.theme.colors.lightblue};
  }
`;

// Dot pulsante: verde = conexao viva, amarelo = stale (>2x intervalo
// de polling, provavel atraso), vermelho = subscription falhou.
// Animacao apenas no estado verde — outros estados ficam estaticos
// pra sinalizar "atencao, nao normal".
export const PulseDot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${(props) =>
    props.$failed
      ? props.theme.colors.red
      : props.$stale
      ? props.theme.colors.yellow || '#f1c40f'
      : props.theme.colors.green || '#2ecc71'};
  box-shadow: 0 0 0 0
    ${(props) =>
      props.$failed
        ? props.theme.colors.red
        : props.$stale
        ? props.theme.colors.yellow || '#f1c40f'
        : props.theme.colors.green || '#2ecc71'};
  animation: ${(props) =>
    props.$failed
      ? 'pulse-red 1.0s ease-out infinite'
      : props.$stale
      ? 'none'
      : 'pulse 1.8s ease-out infinite'};

  @keyframes pulse {
    0% {
      box-shadow: none;
    }
    70% {
      box-shadow: none;
    }
    100% {
      box-shadow: none;
    }
  }
  @keyframes pulse-red {
    0% {
      box-shadow: none;
    }
    70% {
      box-shadow: none;
    }
    100% {
      box-shadow: none;
    }
  }
`;

// Botao flutuante quadrado no canto top-left do mapa, so com icone
// (estilo X/Twitter — sem label de texto, tooltip via title). Padrao
// pra todos os botoes de controle do mapa: tela cheia, recentralizar,
// estilo, bookmarks, som, mini-mapa.
// O <span> de label dentro do botao e escondido via clip rect —
// JSX mantem pra acessibilidade (screen readers), mas visualmente so
// o icone aparece. tooltip no hover via title attr.
const ICON_BUTTON_SIZE = 36;
export const FullscreenButton = styled.button`
  position: absolute;
  top: 12px;
  left: 12px;
  z-index: 5;
  width: ${ICON_BUTTON_SIZE}px;
  height: ${ICON_BUTTON_SIZE}px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  background: ${(props) => props.theme.colors.card};
  border: 1px solid ${(props) => props.theme.colors.disabled};
  border-radius: 6px;
  box-shadow: ${overlayShadow};
  color: ${(props) => props.theme.colors.darkerblue};
  cursor: pointer;
  transition: transform 120ms ease, border-color 120ms ease;

  /* $centered: entra no grupo de botoes do topo-centro (como os demais),
     a ESQUERDA do FitBounds. Em tela cheia isso tira o botao de baixo do
     dock "Top Geracao" (left:16). Em embedded fica no canto sup-esquerdo. */
  ${(props) =>
    props.$centered &&
    `
      left: calc(50% - 128px);
      right: auto;
    `}

  svg {
    width: 16px;
    height: 16px;
    stroke: currentColor;
    stroke-width: 2;
    fill: none;
    stroke-linecap: round;
    stroke-linejoin: round;
  }

  /* Labels de texto dentro do botao: escondidos visualmente, mantidos
     no DOM pra acessibilidade. screen readers e legenda nativa do
     title attr funcionam normal. */
  > span {
    position: absolute;
    width: 1px;
    height: 1px;
    overflow: hidden;
    clip: rect(0 0 0 0);
    white-space: nowrap;
  }

  &:hover {
    transform: translateY(-1px);
    border-color: ${(props) => props.theme.colors.lightblue};
  }

  &:focus-visible {
    outline: 2px solid ${(props) => props.theme.colors.lightblue};
    outline-offset: 2px;
  }
`;

// Botoes de acao no canto SUPERIOR DIREITO do mapa. Antes ficavam
// todos enfileirados a esquerda (left: Npx), agora separamos:
// - Esquerda: FullscreenButton + UpdatedAgo pill (info/state)
// - Direita: FitBounds, MapStyle, Bookmarks, Sound (acoes)
// Layout limpo: state on left, controls on right (padrao Google Maps,
// Mapbox, etc.). 12px da borda direita, 44px de step entre botoes
// (40 do botao + 4 de gap).
/* Posicionamento dos botoes de controle:
   - $centered=true (home /dashboard): centralizados horizontalmente no topo.
     4 botoes x 36px + 3 gaps x 8px = 168px total → ancora em calc(50% +/- N).
   - $centered=false/undef (embedded /dashboard/customer): ancorados a direita
     (mantem comportamento anterior pra nao competir com o painel lateral). */
export const FitBoundsButton = styled(FullscreenButton)`
  left: auto;
  right: 144px;

  ${(props) =>
    props.$centered &&
    `
      left: calc(50% - 84px);
      right: auto;
    `}
`;

export const MapStyleButton = styled(FullscreenButton)`
  left: auto;
  right: 100px;

  ${(props) =>
    props.$centered &&
    `
      left: calc(50% - 40px);
      right: auto;
    `}

  /* Quando ativo (satelite), borda highlight pra indicar o estado */
  ${(props) =>
    props.$active &&
    `
      border-color: ${props.theme.colors.lightblue};
      background: ${
        props.theme.title === 'light'
          ? props.theme.colors.disabled
          : 'rgba(255, 255, 255, 0.06)'
      };
    `}
`;

export const BookmarksButton = styled(FullscreenButton)`
  left: auto;
  right: 56px;

  ${(props) =>
    props.$centered &&
    `
      left: calc(50% + 4px);
      right: auto;
    `}

  ${(props) =>
    props.$active &&
    `
      border-color: ${props.theme.colors.lightblue};
      background: ${
        props.theme.title === 'light'
          ? props.theme.colors.disabled
          : 'rgba(255, 255, 255, 0.06)'
      };
    `}
`;

// Dropdown dos bookmarks — aparece abaixo do BookmarksButton com a
// lista das views salvas + botao 'Salvar visao atual'. Click numa
// view aplica; click no X remove. Posicionamento absoluto relativo
// ao MapContainer (mesmo z-index/altura). Left match com o botao
// (286px) mas adicionamos -100px de offset pra dropdown nao sair
// pela esquerda em telas menores.
export const BookmarksDropdown = styled.div`
  position: absolute;
  top: 56px;
  /* BookmarksButton agora vive em right:56px; alinha o dropdown com
     ele (right:12px alinha pela borda direita do botao). 240px wide
     sobra pra esquerda sem cortar nada. */
  right: 12px;
  z-index: 6;
  width: 240px;

  /* Em modo $centered (home /dashboard) o BookmarksButton ta em
     left:calc(50% + 4px). Posicionamos o dropdown alinhado a borda
     direita do botao (que fica em calc(50% + 40px)) com 240px de
     largura crescendo pra esquerda. */
  ${(props) =>
    props.$centered &&
    `
      right: auto;
      left: calc(50% + 40px - 240px);
    `}
  max-height: 320px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 8px;
  background: ${(props) => props.theme.colors.card};
  border: 1px solid ${(props) => props.theme.colors.disabled};
  border-radius: 6px;
  box-shadow: ${overlayShadow};
  animation: search-dropdown-fade 120ms ease-out;

  ::-webkit-scrollbar {
    width: 4px;
  }
  ::-webkit-scrollbar-thumb {
    background: ${(props) => props.theme.colors.disabled};
    border-radius: 4px;
  }
`;

export const BookmarksSaveButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 7px 10px;
  background: ${(props) => props.theme.colors.lightblue};
  border: 1px solid ${(props) => props.theme.colors.lightblue};
  border-radius: 6px;
  color: ${(props) => props.theme.colors.white};
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  cursor: pointer;
  transition: filter 120ms;
  margin-bottom: 4px;

  svg {
    width: 12px;
    height: 12px;
    stroke: currentColor;
    stroke-width: 2;
    fill: none;
    stroke-linecap: round;
    stroke-linejoin: round;
  }

  &:hover {
    filter: brightness(1.1);
  }
`;

export const BookmarkItem = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 0;
  background: transparent;
`;

export const BookmarkApplyButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  flex: 1;
  min-width: 0;
  padding: 7px 9px;
  background: transparent;
  border: 1px solid ${(props) => props.theme.colors.disabled};
  border-radius: 6px;
  color: ${(props) => props.theme.colors.darkerblue};
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  text-align: left;
  transition: background 120ms, border-color 120ms;

  > .name {
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  svg {
    width: 12px;
    height: 12px;
    stroke: currentColor;
    stroke-width: 2;
    fill: none;
    stroke-linecap: round;
    stroke-linejoin: round;
    color: ${(props) => props.theme.colors.lightblue};
    flex-shrink: 0;
  }

  &:hover {
    background: ${(props) =>
      props.theme.title === 'light'
        ? props.theme.colors.disabled
        : 'rgba(255, 255, 255, 0.05)'};
    border-color: ${(props) => props.theme.colors.lightblue};
  }
`;

export const BookmarkDeleteButton = styled.button`
  flex-shrink: 0;
  width: 26px;
  height: 26px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: 1px solid transparent;
  border-radius: 6px;
  cursor: pointer;
  color: ${(props) => props.theme.colors.label};
  transition: color 120ms, border-color 120ms;

  svg {
    width: 12px;
    height: 12px;
    stroke: currentColor;
    stroke-width: 2;
    fill: none;
    stroke-linecap: round;
    stroke-linejoin: round;
  }

  &:hover {
    color: ${(props) => props.theme.colors.red};
    border-color: ${(props) => props.theme.colors.red};
  }
`;

export const BookmarksEmpty = styled.div`
  padding: 12px 8px;
  font-size: 11px;
  color: ${(props) => props.theme.colors.label};
  text-align: center;
`;


// Popup que abre quando operador clica num cluster ja no zoom maximo
// — lista parques agrupados pra selecao individual. Posicionamento:
// centro-esquerda do mapa (longe das overlays right/top-left).
export const ClusterPopup = styled.div`
  position: absolute;
  top: 56px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 7;
  width: 280px;
  max-height: 320px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 10px;
  background: ${(props) => props.theme.colors.card};
  border: 1px solid ${(props) => props.theme.colors.lightblue};
  border-radius: 6px;
  box-shadow: ${overlayShadow};
  animation: search-dropdown-fade 140ms ease-out;
`;

export const ClusterPopupHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 6px;
  padding: 0 2px 6px;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  color: ${(props) => props.theme.colors.darkerblue};
  border-bottom: 1px solid ${(props) => props.theme.colors.disabled};

  > button {
    background: transparent;
    border: none;
    color: ${(props) => props.theme.colors.label};
    cursor: pointer;
    padding: 4px;
    line-height: 1;
    transition: color 120ms;
    &:hover {
      color: ${(props) => props.theme.colors.red};
    }
    svg {
      width: 12px;
      height: 12px;
      stroke: currentColor;
      stroke-width: 2.5;
      fill: none;
      stroke-linecap: round;
      stroke-linejoin: round;
    }
  }
`;

export const ClusterPopupList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  overflow-y: auto;
  max-height: 260px;

  ::-webkit-scrollbar {
    width: 4px;
  }
  ::-webkit-scrollbar-thumb {
    background: ${(props) => props.theme.colors.disabled};
    border-radius: 4px;
  }
`;

export const ClusterPopupItem = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 7px 10px;
  background: transparent;
  border: 1px solid ${(props) => props.theme.colors.disabled};
  border-radius: 6px;
  cursor: pointer;
  text-align: left;
  width: 100%;
  color: ${(props) => props.theme.colors.darkerblue};
  font-size: 12px;
  transition: all 120ms;

  > .dot {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: ${(props) => props.color};
    flex-shrink: 0;
  }
  > .name {
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-weight: 700;
  }
  > .customer {
    font-size: 10px;
    color: ${(props) => props.theme.colors.label};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 100px;
  }

  &:hover {
    background: ${(props) =>
      props.theme.title === 'light'
        ? props.theme.colors.disabled
        : 'rgba(255, 255, 255, 0.04)'};
    border-color: ${(props) => props.theme.colors.lightblue};
  }
`;


// Toggle de som de alarme. Por padrao desligado (politica de autoplay
// dos browsers bloqueia audio antes de interacao do user, e nem todo
// CCO quer beep). Operador clica pra ativar. Quando ativo, borda
// vermelha — visual de alerta sonoro pronto.
// Botao "Resumo do turno (IA)" — ultimo do grupo de controles do topo.
export const ShiftSummaryButton = styled(FullscreenButton)`
  left: auto;
  right: 188px;

  ${(props) =>
    props.$centered &&
    `
      left: calc(50% + 92px);
      right: auto;
    `}
`;

export const SoundToggleButton = styled(FullscreenButton)`
  left: auto;
  right: 12px;

  ${(props) =>
    props.$centered &&
    `
      left: calc(50% + 48px);
      right: auto;
    `}

  ${(props) =>
    props.$active &&
    `
      border-color: ${props.theme.colors.red};
      background: ${
        props.theme.title === 'light'
          ? props.theme.colors.disabled
          : 'rgba(255, 0, 0, 0.08)'
      };
      color: ${props.theme.colors.red};
    `}
`;

// FullscreenButton foi movido pra antes dos botoes derivados (logo
// antes do FitBoundsButton) pra resolver TDZ: styled(FullscreenButton)
// no module top-level precisa que FullscreenButton ja exista no
// momento da avaliacao.

